import React from 'react';
import { Button, Input, Typography, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { formatNu } from '../../utils/formatAmount';
import './MobileTransferPayPanel.css';

const { Text, Paragraph } = Typography;

export type MobilePayAccount = {
  legalName?: string | null;
  accountName?: string | null;
  accountNumber?: string | null;
  bankName?: string | null;
};

type Props = {
  amountNu: number;
  account: MobilePayAccount;
  journalNumber: string;
  onJournalNumberChange: (value: string) => void;
  payerPhone: string;
  onPayerPhoneChange: (value: string) => void;
  onConfirm?: () => void;
};

/** Build a scannable payment memo for bank apps (amount + account). */
export function buildMobilePayQrPayload(amountNu: number, account: MobilePayAccount): string {
  const amount = Number(amountNu);
  const safeAmount = Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
  const payee = (account.accountName || account.legalName || 'Shop').trim();
  const lines = [
    `Pay Nu. ${safeAmount}`,
    `To: ${payee}`,
  ];
  if (account.accountNumber?.trim()) {
    lines.push(`Account: ${account.accountNumber.trim()}`);
  }
  if (account.bankName?.trim()) {
    lines.push(`Bank: ${account.bankName.trim()}`);
  }
  lines.push('Open mBoB / ePay / bank app, pay this amount, then give the cashier the journal number and your phone number.');
  return lines.join('\n');
}

export function countPhoneDigits(value: string): number {
  return (value.match(/\d/g) || []).length;
}

export function validatePayerPhone(payerPhone: string): string | null {
  if (!payerPhone.trim()) {
    return 'Enter the customer phone number';
  }
  if (countPhoneDigits(payerPhone) < 7) {
    return 'Enter a valid phone number (at least 7 digits)';
  }
  return null;
}

export function validateMobilePayFields(journalNumber: string, payerPhone: string): string | null {
  if (!journalNumber.trim()) {
    return 'Enter the bank journal number';
  }
  return validatePayerPhone(payerPhone);
}

type CompactQrProps = {
  amountNu: number;
  account: MobilePayAccount;
};

/** Compact QR block for split-bill mobile lines (same memo payload as full mobile pay). */
export const MobilePayQrCompact: React.FC<CompactQrProps> = ({ amountNu, account }) => {
  const amount = parseFloat(String(amountNu));
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }

  const payload = buildMobilePayQrPayload(amount, account);
  const payee = (account.accountName || account.legalName || 'Shop').trim();
  const hasAccount = Boolean(account.accountNumber?.trim());

  const copyAmount = async () => {
    try {
      await navigator.clipboard.writeText(amount.toFixed(2));
      message.success('Amount copied');
    } catch {
      message.info(`Amount: ${formatNu(amount)}`);
    }
  };

  const copyAccount = async () => {
    if (!account.accountNumber?.trim()) {
      message.warning('Add the shop bank account in My Profile first');
      return;
    }
    try {
      await navigator.clipboard.writeText(account.accountNumber.trim());
      message.success('Account number copied');
    } catch {
      message.info(account.accountNumber);
    }
  };

  return (
    <div className="mobile-pay-qr-compact">
      <div className="mobile-pay-qr-compact-main">
        <QRCodeSVG value={payload} size={112} level="M" includeMargin />
        <div className="mobile-pay-qr-compact-details">
          <Text className="mobile-pay-qr-compact-amount app-mono">{formatNu(amount)}</Text>
          <Text type="secondary" className="mobile-pay-qr-compact-payee">
            Pay to {payee}
          </Text>
          {hasAccount ? (
            <Text type="secondary" className="mobile-pay-qr-compact-account app-mono">
              {account.accountNumber}
            </Text>
          ) : (
            <Text type="warning" style={{ fontSize: 12 }}>
              Add bank account in Admin → My Profile
            </Text>
          )}
          <div className="mobile-pay-qr-compact-actions">
            <Button type="link" size="small" icon={<CopyOutlined />} onClick={copyAmount}>
              Copy amount
            </Button>
            {hasAccount ? (
              <Button type="link" size="small" icon={<CopyOutlined />} onClick={copyAccount}>
                Copy A/C
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      <Text type="secondary" className="mobile-pay-qr-compact-hint">
        Show customer this QR for this split amount — then enter journal number and phone below.
      </Text>
    </div>
  );
};

export const MobileTransferPayPanel: React.FC<Props> = ({
  amountNu,
  account,
  journalNumber,
  onJournalNumberChange,
  payerPhone,
  onPayerPhoneChange,
  onConfirm,
}) => {
  const payload = buildMobilePayQrPayload(amountNu, account);
  const payee = (account.accountName || account.legalName || 'Shop').trim();
  const hasAccount = Boolean(account.accountNumber?.trim());

  const copyAmount = async () => {
    try {
      await navigator.clipboard.writeText(Number(amountNu).toFixed(2));
      message.success('Amount copied');
    } catch {
      message.info(`Amount: ${formatNu(amountNu)}`);
    }
  };

  const copyAccount = async () => {
    if (!account.accountNumber?.trim()) {
      message.warning('Add the shop bank account in My Profile first');
      return;
    }
    try {
      await navigator.clipboard.writeText(account.accountNumber.trim());
      message.success('Account number copied');
    } catch {
      message.info(account.accountNumber);
    }
  };

  const tryConfirm = () => {
    const err = validateMobilePayFields(journalNumber, payerPhone);
    if (err) {
      message.warning(err);
      return;
    }
    onConfirm?.();
  };

  return (
    <div className="mobile-pay-panel">
      <div className="mobile-pay-amount-box">
        <Text className="mobile-pay-amount-label">Customer pays exactly</Text>
        <div className="mobile-pay-amount-row">
          <Text className="mobile-pay-amount app-mono">{formatNu(amountNu)}</Text>
          <Button type="link" icon={<CopyOutlined />} onClick={copyAmount}>
            Copy
          </Button>
        </div>
      </div>

      <div className="mobile-pay-qr-wrap">
        <QRCodeSVG value={payload} size={168} level="M" includeMargin />
        <Text type="secondary" className="mobile-pay-qr-hint">
          Show this screen to the customer — scan or type the amount in mBoB / bank app
        </Text>
      </div>

      <div className="mobile-pay-account">
        <Text strong>Pay to</Text>
        <Paragraph style={{ marginBottom: 4 }}>{payee}</Paragraph>
        {account.bankName?.trim() ? (
          <Text type="secondary" style={{ display: 'block' }}>
            Bank: {account.bankName.trim()}
          </Text>
        ) : null}
        {hasAccount ? (
          <div className="mobile-pay-account-row">
            <Text className="app-mono">{account.accountNumber}</Text>
            <Button type="link" size="small" icon={<CopyOutlined />} onClick={copyAccount}>
              Copy A/C
            </Button>
          </div>
        ) : (
          <Text type="warning" style={{ display: 'block', marginTop: 4 }}>
            No shop account set — add bank account in Admin → My Profile so cashiers can show A/C + QR details.
          </Text>
        )}
      </div>

      <ol className="mobile-pay-steps">
        <li>Open mBoB, ePay, or your bank app</li>
        <li>Transfer <strong>{formatNu(amountNu)}</strong> to the account above</li>
        <li>Enter the bank <strong>journal number</strong> and payer <strong>phone</strong> below, then Confirm</li>
      </ol>

      <div className="mobile-pay-ref">
        <Text strong>
          Journal number <span style={{ color: '#ff4d4f' }}>*</span>
        </Text>
        <Input
          size="large"
          placeholder="Bank / mBoB journal number"
          value={journalNumber}
          onChange={(e) => onJournalNumberChange(e.target.value)}
          status={!journalNumber.trim() ? 'error' : undefined}
          autoFocus
          allowClear
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          From the customer’s payment success screen — used to trace the transfer
        </Text>
      </div>

      <div className="mobile-pay-ref">
        <Text strong>
          Payer phone <span style={{ color: '#ff4d4f' }}>*</span>
        </Text>
        <Input
          size="large"
          placeholder="e.g. 17123456 or 77654321"
          value={payerPhone}
          onChange={(e) => onPayerPhoneChange(e.target.value)}
          onPressEnter={tryConfirm}
          allowClear
          inputMode="tel"
          status={
            !payerPhone.trim() || countPhoneDigits(payerPhone) < 7 ? 'error' : undefined
          }
        />
        {!payerPhone.trim() || countPhoneDigits(payerPhone) < 7 ? (
          <Text type="danger" style={{ fontSize: 12 }}>
            Enter the full phone number (at least 7 digits). Now: {countPhoneDigits(payerPhone)} digit
            {countPhoneDigits(payerPhone) === 1 ? '' : 's'}.
          </Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Required — so staff can call if the amount is not credited
          </Text>
        )}
      </div>
    </div>
  );
};

export default MobileTransferPayPanel;
